import classNames from "classnames";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { useToast } from "hooks/useToast";
import React from "react";
import {
  ControlProps,
  MultiValue,
  SingleValue,
  components,
} from "react-select";
import BookkeepingSoftwareInkle from "static/images/BookkeepingSoftwareInkle.svg";
import BookkeepingSoftwareQuickbooks from "static/images/BookkeepingSoftwareQuickbooks.svg";
import BookkeepingSoftwareWaveAccounting from "static/images/BookkeepingSoftwareWaveAccounting.svg";
import BookkeepingSoftwareXero from "static/images/BookkeepingSoftwareXero.svg";
import BookkeepingSoftwareZoho from "static/images/BookkeepingSoftwareZoho.svg";
import { useUpdateEntityMutation } from "store/apis/bookkeepingTracker";
import { BookkeepingSoftwareType } from "types/Models/bookkeepingTracker";

export const bookkeepingSoftwareOptions = {
  "Inkle Books": {
    label: (
      <div className="t-text-subtext t-text-text-100 t-flex t-items-center t-gap-2">
        <img src={BookkeepingSoftwareInkle} alt="BookkeepingSoftwareInkle" />
        <div>Inkle Books</div>
      </div>
    ),
    title: "Inkle Books",
    imgUrl: BookkeepingSoftwareInkle,
    value: "Inkle Books",
  },
  QuickBooks: {
    label: (
      <div className="t-text-subtext t-text-text-100 t-flex t-items-center t-gap-2">
        <img
          src={BookkeepingSoftwareQuickbooks}
          alt="BookkeepingSoftwareQuickbooks"
        />
        <div>QuickBooks</div>
      </div>
    ),
    title: "QuickBooks",
    imgUrl: BookkeepingSoftwareQuickbooks,
    value: "QuickBooks",
  },
  Wave: {
    label: (
      <div className="t-text-subtext t-text-text-100 t-flex t-items-center t-gap-2">
        <img
          src={BookkeepingSoftwareWaveAccounting}
          alt="BookkeepingSoftwareWaveAccounting"
        />
        <div>Wave Accounting</div>
      </div>
    ),
    title: "Wave Accounting",
    imgUrl: BookkeepingSoftwareWaveAccounting,
    value: "Wave",
  },
  Xero: {
    label: (
      <div className="t-text-subtext t-text-text-100 t-flex t-items-center t-gap-2">
        <img src={BookkeepingSoftwareXero} alt="BookkeepingSoftwareXero" />
        <div>Xero Accounting</div>
      </div>
    ),
    title: "Xero Accounting",
    imgUrl: BookkeepingSoftwareXero,
    value: "Xero",
  },
  "Zoho Books": {
    label: (
      <div className="t-text-subtext t-text-text-100 t-flex t-items-center t-gap-2">
        <img src={BookkeepingSoftwareZoho} alt="BookkeepingSoftwareZoho" />
        <div>Zoho Books</div>
      </div>
    ),
    title: "Zoho Books",
    imgUrl: BookkeepingSoftwareZoho,
    value: "Zoho Books",
  },
};

export const BookkeepingTrackerSoftware = ({
  entityId,
  groupId,
  bookkeepingSoftware = "Inkle Books",
}: {
  groupId: string;
  entityId: string;
  bookkeepingSoftware: BookkeepingSoftwareType;
}) => {
  const { alertToast, successToast } = useToast();
  const [updateBookkeepingSoftware, { isLoading }] = useUpdateEntityMutation();

  const handleSelectBookkeepingSoftware = async (
    bookkeeping_software: BookkeepingSoftwareType
  ) => {
    try {
      await updateBookkeepingSoftware({
        groupId,
        entityId,
        bookkeeping_software,
      }).unwrap();

      successToast({ message: "Bookkeeping software updated successfully!" });
    } catch (error: any) {
      alertToast({ message: error?.response?.data?.error?.message });
    }
  };

  return (
    <div
      className="t-min-w-full t-max-w-full t-h-full t-flex t-items-center"
      onClick={(e) => e.stopPropagation()}
    >
      <Combobox
        fullWidth
        options={Object.values(bookkeepingSoftwareOptions)}
        onChange={(
          selectedOption: MultiValue<OptionData> | SingleValue<OptionData>
        ) =>
          handleSelectBookkeepingSoftware(
            ((selectedOption as SingleValue<OptionData>)?.value ||
              null) as BookkeepingSoftwareType
          )
        }
        defaultValue={
          bookkeepingSoftware
            ? bookkeepingSoftwareOptions[bookkeepingSoftware]
            : null
        }
        components={{
          ClearIndicator: () => null,
          Control: ({ children, ...props }: ControlProps<OptionData>) => {
            const {
              selectProps: { value },
            } = props;

            return (
              <components.Control
                {...props}
                className={classNames(
                  "t-px-2 t-border t-border-solid hover:t-border-neutral-10 hover:t-bg-surface-grey t-rounded !t-cursor-pointer t-w-full",
                  {
                    " t-border-neutral-0 t-bg-surface-lighter-grey": Boolean(
                      !value
                    ),
                    "t-border-surface-transparent": Boolean(value),
                  }
                )}
              >
                {children}
              </components.Control>
            );
          },
        }}
        controlShouldRenderValue={Boolean(bookkeepingSoftware)}
        isLoading={isLoading}
      />
    </div>
  );
};
